"use client"

import type React from "react"
import { useState, useMemo } from "react"
import { Trash2, Plus } from "lucide-react"
import type { ConnectorData} from "../../../../types/edit-connector-wizard"

interface FieldMappingStepProps {
  connectorData: ConnectorData
  fieldMappings: Record<string, string>
  sourceFields: string[]
  targetFields: string[]
  onUpdateMapping: (sourceField: string, targetField: string) => void
  onRemoveMapping: (sourceField: string) => void
  onAddMapping: (sourceField: string, targetField: string) => boolean
}

const FieldMappingStep: React.FC<FieldMappingStepProps> = ({
  connectorData,
  fieldMappings,
  sourceFields,
  targetFields,
  onUpdateMapping,
  onRemoveMapping,
  onAddMapping,
}) => {
  const [showAddForm, setShowAddForm] = useState(false)
  const [newSourceField, setNewSourceField] = useState("")
  const [newTargetField, setNewTargetField] = useState("")

  // Get available source fields (not already mapped)
  const availableSourceFields = useMemo(() => {
    return sourceFields.filter((field) => !fieldMappings[field])
  }, [sourceFields, fieldMappings])

  const handleAddMapping = () => {
    if (newSourceField && newTargetField) {
      const success = onAddMapping(newSourceField, newTargetField)
      if (success) {
        setNewSourceField("")
        setNewTargetField("")
        setShowAddForm(false)
      }
    }
  }

  const handleCancelAdd = () => {
    setNewSourceField("")
    setNewTargetField("")
    setShowAddForm(false)
  }

  const mappingCount = Object.keys(fieldMappings).length
  const getObjectName = (name: string, isLower:boolean) =>{
    const objectName = name && name === 'source_object' ? connectorData.source_object || 'contact' : name && name === 'target_object' ? connectorData.target_object || 'lead': 'contact';
    return isLower ? objectName.replace(objectName.charAt(0),objectName.charAt(0).toUpperCase()) : objectName.replace(objectName.charAt(0),objectName.charAt(0).toLowerCase())
  }

  return (
    <div className="field-mapping-step">
      <div className="step-header">
        <h1>Map Your Fields</h1>
      </div>

      <div className="mapping-section">
        <h2>{ getObjectName('source_object', true) }</h2>
        <p className="mapping-description">
          Map { getObjectName('source_object', false) } fields to { getObjectName('target_object', false) } fields to define how your HubSpot { getObjectName('source_object', false) } data will appear in Salesforce { getObjectName('target_object', false) }.
        </p>

        <div className="mapping-headers">
          <div className="source-header">{ getObjectName('source_object', true) } Fields - HubSpot ({sourceFields.length})</div>
          <div className="target-header">{ getObjectName('target_object', true) } Fields - Salesforce ({targetFields.length})</div>
        </div>

        <div className="mappings-list">
          {Object.entries(fieldMappings).map(([sourceField, targetField]) => (
            <div key={sourceField} className="mapping-row">
              <div className="source-field">
                <input type="text" value={sourceField} readOnly className="field-input" />
              </div>
              <div className="mapping-arrow">→</div>
              <div className="target-field">
                <select
                  value={targetField}
                  onChange={(e) => onUpdateMapping(sourceField, e.target.value)}
                  className="field-select"
                >
                  <option value="">Select target field</option>
                  {targetFields.map((field) => (
                    <option key={field} value={field}>
                      {field}
                    </option>
                  ))}
                </select>
              </div>
              <button className="remove-button" onClick={() => onRemoveMapping(sourceField)} title="Remove mapping">
                <Trash2 size={16} />
              </button>
            </div>
          ))}

          {showAddForm && (
            <div className="mapping-row add-form">
              <div className="source-field">
                <select
                  value={newSourceField}
                  onChange={(e) => setNewSourceField(e.target.value)}
                  className="field-select"
                >
                  <option value="">Select source field</option>
                  {availableSourceFields.map((field) => (
                    <option key={field} value={field}>
                      {field}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mapping-arrow">→</div>
              <div className="target-field">
                <select
                  value={newTargetField}
                  onChange={(e) => setNewTargetField(e.target.value)}
                  className="field-select"
                >
                  <option value="">Select target field</option>
                  {targetFields.map((field) => (
                    <option key={field} value={field}>
                      {field}
                    </option>
                  ))}
                </select>
              </div>
              <div className="add-form-actions">
                <button
                  className="save-button"
                  onClick={handleAddMapping}
                  disabled={!newSourceField || !newTargetField}
                >
                  Save
                </button>
                <button className="cancel-button" onClick={handleCancelAdd}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {!showAddForm && availableSourceFields.length > 0 && (
          <button className="add-mapping-button" onClick={() => setShowAddForm(true)}>
            <Plus size={16} />
            Add Field Mapping
          </button>
        )}

        <div className="mapping-summary">
          <div className="summary-stats">
            <div className="stat">
              <span className="stat-label">Total Mappings:</span>
              <span className="stat-value">{mappingCount}</span>
            </div>
            <div className="stat">
              <span className="stat-label">HubSpot Fields:</span>
              <span className="stat-value">{sourceFields.length}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Salesforce Fields:</span>
              <span className="stat-value">{targetFields.length}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FieldMappingStep
